import { useCallback, useEffect, useRef } from "react";
import { startActivitySession, endActivitySession, recordItemEvent } from "@music-app/persistence";

/**
 * Manages a listen activity_session + per-item item_events.
 *
 * Starts a session when `enabled` is true. Ends it on cleanup or when
 * `enabled` becomes false. Call `recordPlay(itemId)` for each item played;
 * the nodeId is derived automatically from the itemId prefix.
 */
export function useListenSession(skillId: string, enabled: boolean, nodeId?: string) {
  const sessionIdRef = useRef<string | null>(null);
  const itemCountRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    itemCountRef.current = 0;
    let active = true;
    startActivitySession("listen", nodeId ?? null, skillId).then((id) => {
      if (active) {
        sessionIdRef.current = id;
      } else {
        void endActivitySession(id, 0);
      }
    });
    return () => {
      active = false;
      if (sessionIdRef.current) {
        void endActivitySession(sessionIdRef.current, itemCountRef.current);
        sessionIdRef.current = null;
      }
    };
  // skillId and nodeId are stable for the lifetime of a drill page mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, skillId, nodeId]);

  /** Call once per item play. nodeId is derived from itemId automatically. */
  const recordPlay = useCallback((itemId: string) => {
    const id = sessionIdRef.current;
    if (!id) return;
    itemCountRef.current += 1;
    void recordItemEvent(id, itemId, skillId, "listen");
  }, [skillId]);

  return { recordPlay };
}
